import type { SeoCell, SeoMetric, SeoReport, SeoRow, SeoScale, SeoWindow } from './types';

export type SeoSort = 'catalog' | 'desc' | 'asc';

export function fmt(value: number | null | undefined, digits: number): string {
    if (value == null || Number.isNaN(Number(value))) return '—';
    const negative = value < 0;
    const [whole, frac] = Math.abs(value).toFixed(digits).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const body = digits > 0 ? `${grouped},${frac}` : grouped;
    return negative ? `-${body}` : body;
}

export function fmtDelta(value: number | null | undefined, digits: number): string {
    if (value == null || Number.isNaN(Number(value))) return '—';
    const body = fmt(Math.abs(value), digits);
    if (value > 0) return `+${body}`;
    if (value < 0) return `−${body}`;
    return body;
}

export function esDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!match) return iso;
    return `${match[3]}/${match[2]}/${match[1]}`;
}

export function compareNote(report: SeoReport): string {
    const compare = report.compare;
    const unit = report.grain === 'day' ? 'días' : 'semanas';
    let note = `${compare.before_label} (${compare.before_weeks} ${unit}) frente a ${compare.after_label.toLowerCase()}.`;
    if (!compare.ready) {
        note += report.grain === 'day'
            ? ' La ventana después todavía no tiene dos días completos.'
            : ' La ventana después todavía no tiene dos semanas completas; la columna de la derecha es la última semana cerrada.';
    } else if (!compare.consolidated) {
        note += report.grain === 'day'
            ? ' Menos de siete días: no es una tendencia consolidada.'
            : ' Menos de cuatro semanas: no es una tendencia consolidada.';
    }
    return note;
}

export type DeltaTone = 'up' | 'down' | 'flat';

export function deltaTone(id: string, delta: number | null | undefined): DeltaTone {
    if (delta == null || delta === 0) return 'flat';
    const better = id === 'position' ? delta < 0 : delta > 0;
    return better ? 'up' : 'down';
}

export function positionBand(position: number | null | undefined): number {
    if (position == null) return 0;
    if (position <= 3) return 8;
    if (position <= 5) return 7;
    if (position <= 8) return 6;
    if (position <= 10) return 5;
    if (position <= 15) return 4;
    if (position <= 20) return 3;
    if (position <= 30) return 2;
    return 1;
}

export function metricDigits(metric: SeoMetric): number {
    return metric === 'impressions' ? 0 : 1;
}

export function cellValue(cell: SeoCell | null | undefined, metric: SeoMetric): number | null {
    if (!cell) return null;
    if (metric === 'clicks') return cell.clicks;
    if (metric === 'impressions') return cell.impressions;
    return cell.position;
}

export function compareSeoRows(a: SeoRow, b: SeoRow, metric: SeoMetric, direction: Exclude<SeoSort, 'catalog'>): number {
    if (a.resto !== b.resto) return a.resto ? 1 : -1;
    const left = windowValue(a.after, metric);
    const right = windowValue(b.after, metric);
    if (left == null && right == null) return a.label.localeCompare(b.label, 'es');
    if (left == null) return 1;
    if (right == null) return -1;
    const diff = direction === 'desc' ? right - left : left - right;
    return diff || a.label.localeCompare(b.label, 'es');
}

export function windowValue(bucket: SeoWindow | null | undefined, metric: SeoMetric): number | null {
    if (!bucket) return null;
    if (metric === 'clicks') return bucket.clicks_week ?? null;
    if (metric === 'impressions') return bucket.impressions_week ?? null;
    return bucket.position ?? null;
}

export function heatBackground(
    metric: SeoMetric,
    cell: SeoCell | null | undefined,
    rowMax: number,
    commonMax: number,
    scale: SeoScale,
): string | undefined {
    if (!cell) return undefined;
    if (metric === 'position') {
        const step = positionBand(cell.position) / 8;
        return `color-mix(in srgb, var(--seo-heat) ${Math.round(step * 100)}%, hsl(var(--card)))`;
    }
    const value = metric === 'clicks' ? cell.clicks : cell.impressions;
    const max = scale === 'common' ? commonMax : rowMax;
    const weight = max > 0 ? Math.max(value / max, value ? 0.12 : 0) : 0;
    return `color-mix(in srgb, var(--seo-heat) ${Math.round(Math.min(1, weight) * 100)}%, hsl(var(--card)))`;
}

export function rowMax(cells: (SeoCell | null)[] | undefined, metric: SeoMetric): number {
    let max = 0;
    for (const cell of cells ?? []) {
        if (!cell) continue;
        const value = metric === 'clicks' ? cell.clicks : cell.impressions;
        if (value > max) max = value;
    }
    return max;
}
